import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Alert,
  CircularProgress,
  Stack,
  Paper,
  Chip,
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  Close as CloseIcon,
  Send as SendIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { useBugReport } from '../hooks/useBugReport';

interface BugReportForm {
  bugDescription: string;
  howItHappened: string;
  isBlocking: 'yes' | 'no' | '';
  images: File[];
}

const BugReportPage: React.FC = () => {
  const { submitBugReport, isLoading, error, success } = useBugReport();
  const [formData, setFormData] = useState<BugReportForm>({
    bugDescription: '',
    howItHappened: '',
    isBlocking: '',
    images: [],
  });
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const handleTextChange =
    (field: keyof BugReportForm) =>
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setFormData((prev) => ({ ...prev, [field]: event.target.value }));
      // Clear validation error for this field
      if (validationErrors[field]) {
        setValidationErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors[field];
          return newErrors;
        });
      }
    };

  const handleRadioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({ ...prev, isBlocking: event.target.value as 'yes' | 'no' }));
    if (validationErrors.isBlocking) {
      setValidationErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.isBlocking;
        return newErrors;
      });
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files) {
      const newFiles = Array.from(files).filter((file) => {
        // Validate file type
        const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
        if (!validTypes.includes(file.type)) {
          return false;
        }
        // Validate file size (max 5MB)
        if (file.size > 5 * 1024 * 1024) {
          return false;
        }
        return true;
      });

      setFormData((prev) => ({
        ...prev,
        images: [...prev.images, ...newFiles].slice(0, 5), // Max 5 images
      }));
    }
    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removeImage = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index),
    }));
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.bugDescription.trim()) {
      errors.bugDescription = 'Please explain the bug you encountered';
    } else if (formData.bugDescription.trim().length < 20) {
      errors.bugDescription = 'Please provide more details (at least 20 characters)';
    }

    if (!formData.howItHappened.trim()) {
      errors.howItHappened = 'Please describe how the bug happened';
    } else if (formData.howItHappened.trim().length < 20) {
      errors.howItHappened = 'Please provide more details (at least 20 characters)';
    }

    if (!formData.isBlocking) {
      errors.isBlocking = 'Please indicate if this bug is blocking you';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    submitBugReport({
      bugDescription: formData.bugDescription,
      howItHappened: formData.howItHappened,
      isBlocking: formData.isBlocking === 'yes',
      images: formData.images,
    });

    // Reset form on success (the hook will show the notification)
    if (success) {
      setFormData({
        bugDescription: '',
        howItHappened: '',
        isBlocking: '',
        images: [],
      });
    }
  };

  return (
    <Container maxWidth={false} disableGutters>
      <Box sx={{ mb: 4 }}>
        <Alert severity="info" icon={<InfoIcon />} sx={{ mb: 3 }}>
          <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
            Help us improve Lecturo!
          </Typography>
          <Typography variant="body2">
            Please provide as much detail as possible about the issue you encountered. Include
            specific steps to reproduce the bug, any error messages you saw, and screenshots if
            applicable. The more information you provide, the faster we can fix the issue.
          </Typography>
        </Alert>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Card>
        <CardContent sx={{ p: 4 }}>
          <form onSubmit={handleSubmit}>
            <Stack spacing={3}>
              {/* Bug Description */}
              <FormControl fullWidth>
                <FormLabel
                  component="legend"
                  sx={{ mb: 1, fontWeight: 500, color: 'text.primary' }}
                >
                  1. Explain the bug *
                </FormLabel>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  What went wrong? What did you expect to happen instead?
                </Typography>
                <TextField
                  multiline
                  rows={4}
                  placeholder="Example: When I try to submit a survey response, the page freezes and shows a loading spinner indefinitely. I expected the survey to be submitted successfully."
                  value={formData.bugDescription}
                  onChange={handleTextChange('bugDescription')}
                  error={!!validationErrors.bugDescription}
                  helperText={validationErrors.bugDescription}
                  disabled={isLoading}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'background.paper',
                    },
                  }}
                />
              </FormControl>

              {/* How it Happened */}
              <FormControl fullWidth>
                <FormLabel
                  component="legend"
                  sx={{ mb: 1, fontWeight: 500, color: 'text.primary' }}
                >
                  2. How did the bug happen? *
                </FormLabel>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Please describe the steps that led to this bug. Be as specific as possible.
                </Typography>
                <TextField
                  multiline
                  rows={4}
                  placeholder="Example: 1. I opened the survey from my dashboard
2. I answered all questions
3. I clicked the 'Submit' button at the bottom
4. The loading spinner appeared and never stopped"
                  value={formData.howItHappened}
                  onChange={handleTextChange('howItHappened')}
                  error={!!validationErrors.howItHappened}
                  helperText={validationErrors.howItHappened}
                  disabled={isLoading}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'background.paper',
                    },
                  }}
                />
              </FormControl>

              {/* Is Blocking */}
              <FormControl error={!!validationErrors.isBlocking}>
                <FormLabel
                  component="legend"
                  sx={{ mb: 1, fontWeight: 500, color: 'text.primary' }}
                >
                  3. Is this bug stopping you from using Lecturo properly? *
                </FormLabel>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  This helps us prioritize critical issues.
                </Typography>
                <RadioGroup value={formData.isBlocking} onChange={handleRadioChange}>
                  <FormControlLabel
                    value="yes"
                    control={<Radio />}
                    label="Yes, I cannot continue my work"
                    disabled={isLoading}
                  />
                  <FormControlLabel
                    value="no"
                    control={<Radio />}
                    label="No, I can work around it"
                    disabled={isLoading}
                  />
                </RadioGroup>
                {validationErrors.isBlocking && (
                  <Typography variant="caption" color="error" sx={{ mt: 1 }}>
                    {validationErrors.isBlocking}
                  </Typography>
                )}
              </FormControl>

              {/* Images */}
              <FormControl fullWidth>
                <FormLabel
                  component="legend"
                  sx={{ mb: 1, fontWeight: 500, color: 'text.primary' }}
                >
                  4. Images (Optional)
                </FormLabel>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Screenshots help us understand the issue better. Max 5 images, 5MB each.
                </Typography>

                {formData.images.length > 0 && (
                  <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                    <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                      {formData.images.map((file, index) => (
                        <Chip
                          key={index}
                          label={file.name}
                          onDelete={() => removeImage(index)}
                          deleteIcon={<CloseIcon />}
                          variant="outlined"
                          sx={{ maxWidth: '200px' }}
                        />
                      ))}
                    </Stack>
                  </Paper>
                )}

                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleFileChange}
                  style={{ display: 'none' }}
                  disabled={isLoading || formData.images.length >= 5}
                />
                <Button
                  variant="outlined"
                  startIcon={<CloudUploadIcon />}
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isLoading || formData.images.length >= 5}
                  sx={{ alignSelf: 'flex-start' }}
                >
                  {formData.images.length >= 5
                    ? 'Maximum images reached'
                    : `Upload Images (${formData.images.length}/5)`}
                </Button>
              </FormControl>

              {/* Submit Button */}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  startIcon={isLoading ? <CircularProgress size={20} /> : <SendIcon />}
                  disabled={isLoading}
                  sx={{ minWidth: 150 }}
                >
                  {isLoading ? 'Submitting...' : 'Submit Report'}
                </Button>
              </Box>
            </Stack>
          </form>
        </CardContent>
      </Card>
    </Container>
  );
};

export default BugReportPage;
